import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Toast } from 'primereact/toast';
import { useHistory, useParams } from 'react-router-dom';

import { useAuth } from '../context/auth';
import ApiService from '../services/ApiService';
import {
	buildMachineRecipeAmountGroups,
	machineRecipeAmountsToCsv,
	machineRecipeAmountsToTsv,
} from './machineRecipeAmountsUtils';

import './MachineRecipeAmounts.css';

const api = new ApiService();

const getCupConfigurations = (data) => {
	if (Array.isArray(data)) return data;
	if (Array.isArray(data?.cupConfigurations)) return data.cupConfigurations;
	if (Array.isArray(data?.content)) return data.content;
	return data?.id && data?.cupSize != null ? [data] : [];
};

const formatGrams = (grams) => (Number.isFinite(grams) ? Math.round(grams).toString() : '—');

const MachineRecipeAmounts = () => {
	const { machineId } = useParams();
	const history = useHistory();
	const toast = useRef(null);
	const { authTokens } = useAuth();
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState('');
	const [machine, setMachine] = useState(null);
	const [assignments, setAssignments] = useState([]);
	const [recipes, setRecipes] = useState([]);
	const [cupConfigurations, setCupConfigurations] = useState([]);

	useEffect(() => {
		let active = true;
		const headers = { 'X-Firebase-Auth-Client': authTokens.token };

		const load = async () => {
			setLoading(true);
			setError('');
			try {
				const [machineResponse, assignmentsResponse, recipesResponse, cupsResponse] = await Promise.all([
					api.getMachine(machineId, headers),
					api.getMachineRecipes(machineId, headers),
					api.getAllMachineRecipesWithoutPagination(headers),
					api.getAllCupConfigurations(headers),
				]);
				if (!active) return;

				setMachine(machineResponse.data);
				setAssignments(assignmentsResponse.data || []);
				setRecipes(recipesResponse.data?.recipes || []);
				setCupConfigurations(getCupConfigurations(cupsResponse.data));
			} catch (loadError) {
				if (active) {
					console.error('Failed to load machine recipe amounts:', loadError);
					setError('The recipe amounts could not be loaded. Please try again.');
				}
			} finally {
				if (active) setLoading(false);
			}
		};

		if (authTokens?.token) load();

		return () => {
			active = false;
		};
	}, [authTokens, machineId]);

	const cupConfiguration = useMemo(
		() => cupConfigurations.find((configuration) => configuration.id === machine?.cupConfigurationId),
		[cupConfigurations, machine],
	);
	const recipeGroups = useMemo(
		() => buildMachineRecipeAmountGroups(assignments, recipes, cupConfiguration),
		[assignments, recipes, cupConfiguration],
	);
	const exportTable = (format) => {
		const contents = format === 'csv' ? machineRecipeAmountsToCsv(recipeGroups) : machineRecipeAmountsToTsv(recipeGroups);
		const blob = new Blob([contents], { type: format === 'csv' ? 'text/csv;charset=utf-8' : 'text/tab-separated-values;charset=utf-8' });
		const url = URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = url;
		link.download = `${machine?.name || 'machine'}-recipe-amounts.${format}`;
		link.click();
		URL.revokeObjectURL(url);
	};
	const copyToClipboard = async () => {
		try {
			await navigator.clipboard.writeText(machineRecipeAmountsToTsv(recipeGroups));
			toast.current.show({ severity: 'success', summary: 'Copied', detail: 'Paste directly into Google Sheets.', life: 3000 });
		} catch (copyError) {
			toast.current.show({ severity: 'error', summary: 'Copy failed', detail: 'Select the table and copy it manually.', life: 4000 });
		}
	};

	return (
		<div className="machine-recipe-amounts">
			<Toast ref={toast} />
			<div className="machine-recipe-amounts__header">
				<div>
					<h1 className="m-0 text-900">Recipe ingredient amounts</h1>
					<p className="mt-2 mb-0 text-600">
						{machine?.name || 'Machine'}{cupConfiguration ? ` · ${cupConfiguration.name} (${cupConfiguration.cupSize} g)` : ''}
					</p>
				</div>
				<Button label="Back to recipes" icon="pi pi-arrow-left" outlined onClick={() => history.goBack()} />
			</div>

			{loading ? (
				<div className="machine-recipe-amounts__loading"><ProgressSpinner /></div>
			) : (
				<>
					{error && <Message severity="error" text={error} className="w-full mb-3" />}
					{!error && !cupConfiguration && (
							<Message severity="warn" text="This machine has no cup configuration, so grams cannot be calculated." className="w-full mb-3" />
						)}
					<div className="machine-recipe-amounts__actions">
						<Button label="Copy for Google Sheets" icon="pi pi-copy" onClick={copyToClipboard} disabled={recipeGroups.length === 0} />
						<Button label="Download CSV" icon="pi pi-download" outlined onClick={() => exportTable('csv')} disabled={recipeGroups.length === 0} />
					</div>
					<p className="text-600 mt-0 mb-3">Ingredient and water grams use the same recipe calculation as the recipe editor and this machine’s cup configuration. Blend and steam values are milliseconds.</p>
					{recipeGroups.length === 0 ? (
						<Message severity="info" text="No recipes with ingredients are assigned to this machine." className="w-full" />
					) : (
						<div className="machine-recipe-amounts__table-wrapper">
							<table className="machine-recipe-amounts__table" aria-label="Ingredient percentages and recipe properties for recipes assigned to this machine">
								<thead>
									<tr><th>Recipe</th><th>Ingredient</th><th>Percentage (%)</th><th>Grams (g)</th><th>Recipe type</th><th>Water (g)</th><th>Total ingredients (g)</th><th>Weight ratio</th><th>Water ratio</th><th>Blend (ms)</th><th>Steam (ms)</th></tr>
								</thead>
								{recipeGroups.map((group) => (
									<tbody className="machine-recipe-amounts__recipe-group" key={group.recipeId}>
										{group.ingredients.map((ingredient, index) => (
											<tr key={`${ingredient.ingredientName}-${index}`}>
												<td>{index === 0 ? group.recipeName : ''}</td>
												<td>{ingredient.ingredientName}</td>
												<td>{ingredient.percentage}</td>
												<td>{formatGrams(ingredient.grams)}</td>
												<td>{index === 0 ? group.recipeType : ''}</td>
												<td>{index === 0 ? formatGrams(group.waterGrams) : ''}</td>
												<td>{index === 0 ? formatGrams(group.totalIngredientGrams) : ''}</td>
												<td>{index === 0 ? group.weightRatio : ''}</td>
												<td>{index === 0 ? group.waterRatio : ''}</td>
												<td>{index === 0 ? group.blendConfiguration : ''}</td>
												<td>{index === 0 ? group.steamConfiguration : ''}</td>
											</tr>
										))}
									</tbody>
									))}
							</table>
						</div>
					)}
				</>
			)}
		</div>
	);
};

export default MachineRecipeAmounts;
